import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Users,
  UserPlus,
  Trash2,
  Mail,
  Plus,
  X,
  SlidersHorizontal,
  TrendingUp,
} from 'lucide-react';
import Spinner from '../../components/ui/Spinner';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Modal from '../../components/ui/Modal';
import { getErrorMessage, isLimitReached } from '../../lib/apiClient';
import { formatDate } from '../../utils/format';
import { useAuthStore } from '../../store/authStore';
import { useTenant } from '../../hooks/useTenant';
import {
  acceptMyInvitation,
  createInvitation,
  getInvitations,
  getMembers,
  getMemberWorkspaces,
  getMyInvitations,
  removeMember,
  revokeInvitation,
  setMemberWorkspaces,
  tenantKeys,
} from './tenancyApi';
import { WORKSPACE_PERMISSIONS, WORKSPACE_ROLES, roleLabel } from './permissions';

const emptyRow = (workspaceId) => ({
  workspaceId: workspaceId ?? null,
  role: 'member',
  permissions: ['view'],
});

/** Editable list of workspace assignments (workspace + tier + granular permissions). */
function AssignmentRows({ rows, setRows, workspaces }) {
  const usedIds = new Set(rows.map((r) => r.workspaceId).filter(Boolean));
  const update = (i, patch) =>
    setRows(
      rows.map((r, idx) => (idx === i ? { ...r, ...patch, preserveExistingActions: false } : r)),
    );
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const togglePerm = (i, key) => {
    const r = rows[i];
    const has = r.permissions.includes(key);
    update(i, {
      permissions: has ? r.permissions.filter((k) => k !== key) : [...r.permissions, key],
    });
  };
  const addRow = () => {
    const next = workspaces.find((w) => !usedIds.has(w.id));
    setRows([...rows, emptyRow(next?.id ?? null)]);
  };
  const allUsed = usedIds.size >= workspaces.length;

  return (
    <div className="space-y-3">
      {rows.map((row, i) => {
        const options = workspaces
          .filter((w) => w.id === row.workspaceId || !usedIds.has(w.id))
          .map((w) => ({
            value: w.id,
            label: `${w.name}${w.type === 'personal' ? ' (Personal)' : ''}`,
          }));
        return (
          <div key={i} className="rounded-lg border border-slate-200 bg-slate-100 p-3">
            <div className="flex flex-wrap items-end gap-2">
              <Select
                label="Workspace"
                className="min-w-[160px] flex-1"
                value={row.workspaceId}
                onChange={(id) => update(i, { workspaceId: id })}
                options={options}
              />
              <Select
                label="Role"
                className="min-w-[120px]"
                value={row.role}
                onChange={(role) => update(i, { role })}
                options={WORKSPACE_ROLES.map((r) => ({
                  value: r.value,
                  label: r.label,
                }))}
              />
              {rows.length > 1 && (
                <Button variant="ghost" size="sm" type="button" onClick={() => removeRow(i)}>
                  <X className="h-4 w-4 text-slate-400" />
                </Button>
              )}
            </div>
            {row.preserveExistingActions && row.customActions?.length > 0 && (
              <p className="mt-3 text-xs text-slate-600">
                Custom access:{' '}
                {row.customActions
                  .map(
                    (a) =>
                      ({
                        'file.read': 'view files',
                        'folder.read': 'view folders',
                        'file.create': 'upload files',
                        'folder.create': 'create folders',
                        'file.write': 'edit files',
                        'folder.manage': 'manage folders',
                        'file.download': 'download files',
                        'file.delete': 'delete files',
                      })[a] ?? a.replace('.', ' '),
                  )
                  .join(', ')}
                . Saving unchanged keeps this access. Changing a checkbox replaces it with the
                selected permissions.
              </p>
            )}
            {row.role === 'member' ? (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {WORKSPACE_PERMISSIONS.map((p) => (
                  <label
                    key={p.key}
                    className="flex items-center gap-1.5 text-xs text-slate-600"
                    title={p.hint}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600"
                      checked={row.permissions.includes(p.key)}
                      onChange={() => togglePerm(i, p.key)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-400">
                Admins get full access to all features in this workspace (never billing or org
                settings).
              </p>
            )}
          </div>
        );
      })}
      {!allUsed && (
        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-slate-100"
        >
          <Plus className="h-3.5 w-3.5" />
          Add workspace
        </button>
      )}
    </div>
  );
}

/** Edit an existing member's workspace assignments + permissions after they've joined. */
function ManageAccessModal({ open, onClose, orgId, member, workspaces }) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState([]);

  const assignmentsQuery = useQuery({
    queryKey: tenantKeys.memberWorkspaces(orgId, member?.id),
    queryFn: () => getMemberWorkspaces({ orgId, memberId: member.id }),
    enabled: open && Boolean(member?.id),
  });

  useEffect(() => {
    if (assignmentsQuery.data) {
      setRows(
        assignmentsQuery.data.map((a) => ({
          workspaceId: a.workspaceId,
          role: a.role,
          permissions: a.permissions ?? [],
          customActions: a.customActions ?? [],
          preserveExistingActions: Boolean(a.customActions?.length),
        })),
      );
    }
  }, [assignmentsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      setMemberWorkspaces({
        orgId,
        memberId: member.id,
        assignments: rows
          .filter((r) => r.workspaceId)
          .map((r) => ({
            workspaceId: r.workspaceId,
            role: r.role,
            permissions: r.role === 'member' ? r.permissions : undefined,
            preserveExistingActions: r.preserveExistingActions || undefined,
          })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: tenantKeys.memberWorkspaces(orgId, member.id),
      });
      queryClient.invalidateQueries({ queryKey: tenantKeys.members(orgId) });
      toast.success('Access updated');
      onClose();
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update access')),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Manage access — ${member?.user?.displayName ?? member?.user?.email ?? ''}`}
      dismissable={!saveMutation.isPending}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            Save access
          </Button>
        </div>
      }
    >
      {assignmentsQuery.isLoading ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Choose which workspaces this member can access and what they can do in each.
          </p>
          {rows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">
              This member has no workspace access. Add a workspace below.
            </p>
          ) : (
            <AssignmentRows rows={rows} setRows={setRows} workspaces={workspaces} />
          )}
          {rows.length === 0 && (
            <button
              type="button"
              onClick={() => setRows([emptyRow(workspaces[0]?.id)])}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-brand-600 hover:bg-slate-100"
            >
              <Plus className="h-3.5 w-3.5" />
              Add workspace
            </button>
          )}
        </div>
      )}
    </Modal>
  );
}

export default function MembersPage() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { activeOrgId, can, workspaces, inviteCapacity } = useTenant();

  const [inviteEmail, setInviteEmail] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [lastInvited, setLastInvited] = useState(null);
  const [managing, setManaging] = useState(null); // member being edited
  const [limitError, setLimitError] = useState(null); // 422 LIMIT_REACHED body

  // Seed the first assignment row with a real workspace once they load.
  useEffect(() => {
    if (workspaces.length && rows.length === 1 && !rows[0].workspaceId) {
      setRows([emptyRow(workspaces[0].id)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces]);

  const membersQuery = useQuery({
    queryKey: tenantKeys.members(activeOrgId),
    queryFn: () => getMembers(activeOrgId),
    enabled: Boolean(activeOrgId),
  });
  const invitesQuery = useQuery({
    queryKey: tenantKeys.invitations(activeOrgId),
    queryFn: () => getInvitations(activeOrgId),
    enabled: Boolean(activeOrgId) && can.manageInvites,
  });
  // Pending invitations addressed to me (existing-user accept flow).
  const myInvitesQuery = useQuery({
    queryKey: tenantKeys.myInvitations(),
    queryFn: getMyInvitations,
  });

  const invalidateMembers = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKeys.members(activeOrgId),
    });
  const invalidateInvites = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKeys.invitations(activeOrgId),
    });

  const inviteMutation = useMutation({
    mutationFn: () =>
      createInvitation({
        orgId: activeOrgId,
        email: inviteEmail.trim(),
        workspaces: rows
          .filter((r) => r.workspaceId)
          .map((r) => ({
            workspaceId: r.workspaceId,
            role: r.role,
            permissions: r.role === 'member' ? r.permissions : undefined,
          })),
      }),
    onSuccess: (data) => {
      setLastInvited({
        email: inviteEmail.trim(),
        emailSent: data.emailSent,
        userExists: data.userExists,
      });
      setInviteEmail('');
      setRows([emptyRow(workspaces[0]?.id)]);
      setLimitError(null);
      invalidateInvites();
      toast.success(data.emailSent ? 'Invitation email sent' : 'Invitation created');
    },
    onError: (err) => {
      if (isLimitReached(err)) {
        setLimitError(err.response.data);
      } else {
        toast.error(getErrorMessage(err, 'Could not create invitation'));
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      invalidateMembers();
      toast.success('Member removed');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not remove member')),
  });

  const revokeMutation = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      invalidateInvites();
      toast.success('Invitation revoked');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not revoke')),
  });

  const acceptMineMutation = useMutation({
    mutationFn: (invitationId) => acceptMyInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKeys.myInvitations() });
      queryClient.invalidateQueries({ queryKey: tenantKeys.organizations() });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      toast.success('Invitation accepted');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not accept invitation')),
  });

  const members = membersQuery.data ?? [];
  const pendingInvites = (invitesQuery.data ?? []).filter((i) => i.status === 'pending');
  const myPendingInvites = myInvitesQuery.data ?? [];
  const canSubmitInvite = inviteEmail.trim() && rows.some((r) => r.workspaceId);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-800">Members</h1>
      </div>

      {inviteCapacity && (
        <p className="text-sm text-slate-500">
          Seats: {inviteCapacity.used} of{' '}
          {inviteCapacity.currentCapacity ?? inviteCapacity.limit ?? 'unlimited'} in use or reserved
          · {inviteCapacity.included ?? 'Custom'} included
          {inviteCapacity.extraSeats ? ` + ${inviteCapacity.extraSeats} purchased` : ''}.{' '}
          {inviteCapacity.currentCapacity !== inviteCapacity.limit && inviteCapacity.limit != null
            ? `New invitations are limited to ${inviteCapacity.limit} total seats while your plan change is pending. `
            : ''}
          <Link to="/billing" className="text-brand-600 underline">
            Manage seats
          </Link>
        </p>
      )}

      {/* Invitations addressed to me (existing-user accept) */}
      {myPendingInvites.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-brand-200 bg-brand-50">
          <h2 className="border-b border-brand-100 px-4 py-3 text-sm font-semibold text-slate-700">
            Invitations for you
          </h2>
          {myPendingInvites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-3 border-b border-brand-100 px-4 py-3"
            >
              <Mail className="h-4 w-4 text-brand-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-slate-700">
                  Join{' '}
                  <span className="font-medium">
                    {inv.workspaces?.map((w) => w.name).join(', ') || 'an organization'}
                  </span>
                </p>
                <p className="text-xs text-slate-400">Expires {formatDate(inv.expiresAt)}</p>
              </div>
              <Button
                size="sm"
                loading={acceptMineMutation.isPending}
                onClick={() => acceptMineMutation.mutate(inv.id)}
              >
                Accept
              </Button>
            </div>
          ))}
        </section>
      )}

      {/* Invite (org admins/owner) */}
      {can.invite && (
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <UserPlus className="h-4 w-4" /> Invite a member
          </h2>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmitInvite) inviteMutation.mutate();
            }}
          >
            {limitError && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <TrendingUp className="h-4 w-4 shrink-0" />
                <span className="flex-1">{limitError.message}</span>
                <Link to="/billing" className="shrink-0 font-medium text-brand-600 hover:underline">
                  Upgrade plan →
                </Link>
              </div>
            )}
            <div className="max-w-md">
              <Input
                label="Email"
                type="email"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  if (limitError) setLimitError(null);
                }}
              />
            </div>
            <div>
              <p className="mb-1.5 text-xs font-medium text-slate-500">Workspace access</p>
              <AssignmentRows rows={rows} setRows={setRows} workspaces={workspaces} />
            </div>
            <Button type="submit" loading={inviteMutation.isPending} disabled={!canSubmitInvite}>
              Send invite
            </Button>
          </form>

          {lastInvited && (
            <div
              className={
                lastInvited.emailSent
                  ? 'mt-3 rounded-lg bg-brand-50 p-3 text-sm text-slate-700'
                  : 'mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'
              }
            >
              {lastInvited.emailSent ? (
                <p>
                  Invitation email sent to <span className="font-medium">{lastInvited.email}</span>
                  {lastInvited.userExists
                    ? ' — they can accept from their account.'
                    : ' — it expires in 7 days.'}
                </p>
              ) : (
                <p>
                  The invitation for <span className="font-medium">{lastInvited.email}</span> was
                  created but the email could not be sent. Revoke it below and try again.
                </p>
              )}
            </div>
          )}
        </section>
      )}

      {/* Pending invitations */}
      {can.manageInvites && pendingInvites.length > 0 && (
        <section className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
          <h2 className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
            Pending invitations
          </h2>
          {pendingInvites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-3 border-b border-slate-100 px-4 py-3"
            >
              <Mail className="h-4 w-4 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-slate-700">{inv.email}</p>
                <p className="text-xs text-slate-400">
                  {(inv.workspaces ?? [])
                    .map((w) => `${w.name} (${roleLabel(w.role)})`)
                    .join(', ') || 'Organization'}{' '}
                  · expires {formatDate(inv.expiresAt)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  revokeMutation.mutate({
                    orgId: activeOrgId,
                    invitationId: inv.id,
                  })
                }
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
        </section>
      )}

      {/* Members */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-700">Organization members</h2>
        </div>
        {membersQuery.isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : (
          members.map((m) => {
            const isSelf = m.userId === currentUserId;
            const isInvited = m.accessScope === 'assigned';
            return (
              <div
                key={m.id}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-700">
                    {m.user?.displayName ?? m.user?.email ?? m.userId}
                    {isSelf ? ' (you)' : ''}
                  </p>
                  <p className="text-xs text-slate-400">
                    {m.user?.email ?? ''} · {isInvited ? 'Invited member' : roleLabel(m.role)}
                  </p>
                </div>
                {can.manageMembers && isInvited && (
                  <Button variant="secondary" size="sm" onClick={() => setManaging(m)}>
                    <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
                    Manage access
                  </Button>
                )}
                {!isInvited && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                    {roleLabel(m.role)}
                  </span>
                )}
                {can.manageMembers && !isSelf && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      removeMutation.mutate({
                        orgId: activeOrgId,
                        memberId: m.id,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            );
          })
        )}
      </section>

      <ManageAccessModal
        open={Boolean(managing)}
        onClose={() => setManaging(null)}
        orgId={activeOrgId}
        member={managing}
        workspaces={workspaces}
      />
    </div>
  );
}
